import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"

interface AdminDayFormLabels {
  heading: string
  labelField: string
  labelOptional: string
  labelHint: string
  save: string
}

interface AdminDayFormProps {
  // `null` when the day carries no label yet -- normalized to "" for the
  // form's own local state below (an uncontrolled-to-controlled input
  // would otherwise warn on first keystroke).
  label: string | null
  subtitle: string
  // The day's own screen (GET /admin/days/:date) doubles as this form's
  // submit target (PATCH, same address) and its back link -- one prop,
  // not two, since both are genuinely the same URL (Trip::
  // AdminTripController's own trip form takes the same "reuse the
  // resource's own address" shape for the analogous case).
  backHref: string
  backLabel: string
  labels: AdminDayFormLabels
}

// The form's own local state stays flat, keyed by the model's bare
// attribute name (`label`), matching Trip::AdminTripController's own
// reasoning: the server's failed-save `errors` prop round-trips under
// that same bare key, and the wire payload nests it under "day" only at
// submit time (via `transform`), for Rails 8's
// `params.expect(day: [:label])`.
interface DayFormData {
  label: string
  authenticity_token: string
}

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"

// The day-label form (t009, `Schedule::AdminDaysController#edit`/
// `#update`): docs/design/admin.html's "State 5" verbatim -- the single
// optional «Лейбл дня» field and its hint, no date field (the date is
// derived, never editable -- FR-15) and no delete control (a day is
// never destroyed from here, only by falling out of the trip's range on
// a trip save, FR-12).
export default function AdminDayForm({ label, subtitle, backHref, backLabel, labels }: AdminDayFormProps) {
  const form = useForm<DayFormData>({
    label: label ?? "",
    authenticity_token: csrfToken(),
  })

  form.transform((data) => ({
    day: { label: data.label },
    authenticity_token: data.authenticity_token,
  }))

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    form.patch(backHref)
  }

  return (
    <AdminLayout>
      <Link href={backHref} className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {backLabel}
      </Link>
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[22px] font-bold leading-7 text-gray-900">{labels.heading}</h2>
        <p className="text-[13px] leading-[18px] text-gray-500">{subtitle}</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="flex items-baseline gap-2 text-[13px] font-medium leading-[18px] text-gray-700">
            {labels.labelField}
            <span className="text-xs font-normal text-gray-400">{labels.labelOptional}</span>
          </span>
          <input
            type="text"
            value={form.data.label}
            onChange={(event) => form.setData("label", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className="text-xs leading-4 text-gray-400">{labels.labelHint}</span>
          {form.errors.label && <span className="text-xs leading-4 text-red-700">{form.errors.label}</span>}
        </label>
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
      </form>
    </AdminLayout>
  )
}
